import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import {
  Button,
  Group,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  Select,
  TextInput,
} from "@mantine/core";
import toast from "react-hot-toast";

const PREFERENCE_ROWS = [
  { category: "Approval (MCA)", event: "New Approval Request", key: "mca_new" },
  { category: "Approval (MCA)", event: "Step Approved", key: "mca_approved" },
  { category: "Approval (MCA)", event: "Step Rejected", key: "mca_rejected" },
  { category: "Tickets", event: "Ticket Assigned", key: "ticket_assigned" },
  { category: "System", event: "System Announcement", key: "system_announcement" },
];

const defaultPrefs = () => ({
  channels: PREFERENCE_ROWS.reduce((acc, row) => {
    acc[row.key] = { inApp: true, email: true, whatsapp: false, sms: false, push: false };
    return acc;
  }, {}),
  quietHours: {
    enabled: false,
    start: "22:00",
    end: "08:00",
    timezone: "Asia/Hong_Kong",
  },
});

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(defaultPrefs());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("notificationPreferences");
    if (stored) {
      try {
        setPrefs({ ...defaultPrefs(), ...JSON.parse(stored) });
      } catch (_) {
        /* ignore */
      }
    }
  }, []);

  const toggleChannel = (key, channel) => {
    setPrefs((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [key]: { ...prev.channels[key], [channel]: !prev.channels[key][channel] },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    localStorage.setItem("notificationPreferences", JSON.stringify(prefs));
    setSaving(false);
    toast.success("Preferences saved");
  };

  return (
    <Stack p="md" spacing="lg">
      <Title order={3}>{i18n.t("Notification Preferences")}</Title>

      <Paper p="md" withBorder>
        <Text weight={600} mb="md">
          Channel Matrix
        </Text>
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>{i18n.t("Category")}</th>
              <th>{i18n.t("Event")}</th>
              <th>{i18n.t("In-App")}</th>
              <th>{i18n.t("Email")}</th>
              <th>{i18n.t("WhatsApp")}</th>
              <th>SMS</th>
              <th>{i18n.t("Push")}</th>
            </tr>
          </thead>
          <tbody>
            {PREFERENCE_ROWS.map((row) => (
              <tr key={row.key}>
                <td>{row.category}</td>
                <td>{row.event}</td>
                {["inApp", "email", "whatsapp", "sms", "push"].map((ch) => (
                  <td key={ch}>
                    <Switch
                      size="xs"
                      checked={prefs.channels[row.key]?.[ch] ?? false}
                      disabled={["whatsapp", "sms", "push"].includes(ch)}
                      onChange={() => toggleChannel(row.key, ch)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <Text size="xs" color="dimmed" mt="sm">
          WhatsApp, SMS, and Push channels are planned for a future release.
        </Text>
      </Paper>

      <Paper p="md" withBorder>
        <Text weight={600} mb="md">
          Quiet Hours
        </Text>
        <Stack spacing="sm">
          <Switch
            label="Enable quiet hours"
            checked={prefs.quietHours.enabled}
            onChange={(e) =>
              setPrefs((prev) => ({
                ...prev,
                quietHours: { ...prev.quietHours, enabled: e.currentTarget.checked },
              }))
            }
          />
          <Group>
            <TextInput
              label="Start"
              type="time"
              value={prefs.quietHours.start}
              onChange={(e) =>
                setPrefs((prev) => ({
                  ...prev,
                  quietHours: { ...prev.quietHours, start: e.target.value },
                }))
              }
            />
            <TextInput
              label="End"
              type="time"
              value={prefs.quietHours.end}
              onChange={(e) =>
                setPrefs((prev) => ({
                  ...prev,
                  quietHours: { ...prev.quietHours, end: e.target.value },
                }))
              }
            />
            <Select
              label="Timezone"
              data={[{ value: "Asia/Hong_Kong", label: "Asia/Hong_Kong (GMT+8)" }]}
              value={prefs.quietHours.timezone}
              onChange={(v) =>
                setPrefs((prev) => ({
                  ...prev,
                  quietHours: { ...prev.quietHours, timezone: v },
                }))
              }
            />
          </Group>
        </Stack>
      </Paper>

      <Group>
        <Button onClick={handleSave} loading={saving}>
          Save Preferences
        </Button>
      </Group>
    </Stack>
  );
};

export default NotificationPreferences;
